import { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { CollectionPaths, EventDocument } from 'shared_schemas';
import { Link } from 'react-router-dom';
import { StartggImportModal } from '../components/events/StartggImportModal';

interface EventData extends EventDocument {
    id: string; // Ensure ID is present
}

export default function AdminEventsPage() {
    const [events, setEvents] = useState<EventData[]>([]);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [filter, setFilter] = useState<'upcoming' | 'past' | 'all'>('upcoming');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let q;
        const today = new Date().toISOString().slice(0, 10);

        if (filter === 'upcoming') {
            q = query(collection(db, CollectionPaths.events), where('date', '>=', today), orderBy('date', 'asc'));
        } else if (filter === 'past') {
            q = query(collection(db, CollectionPaths.events), where('date', '<', today), orderBy('date', 'desc'));
        } else {
            q = query(collection(db, CollectionPaths.events), orderBy('date', 'desc'));
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as EventData);
            setEvents(docs);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [filter]);

    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case 'active': return 'badge-success';
            case 'completed': return 'badge-warning'; // or secondary
            default: return 'badge-primary'; // scheduled
        }
    };

    return (
        <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-display font-bold text-white">Event Manager</h1>
                    <p className="text-secondary-400">Manage practices, tournaments, and matches.</p>
                </div>
                <div className="flex space-x-3">
                    <button
                        onClick={() => setImportModalOpen(true)}
                        className="btn-secondary flex items-center gap-2"
                    >
                        <span>⬇️</span> Import from Start.gg
                    </button>
                    <button className="btn-primary flex items-center gap-2">
                        <span>➕</span> Create Event
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2">
                {(['upcoming', 'past', 'all'] as const).map(f => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f
                            ? 'bg-primary-500 text-white shadow-pirate'
                            : 'bg-secondary-800 text-secondary-300 hover:bg-secondary-700 hover:text-white'
                            }`}
                    >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                ))}
            </div>

            {/* Events Grid */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {events.map((event) => (
                        <Link key={event.id} to={`/app/admin/events/${event.id}`}>
                            <div className="card-hover cursor-pointer h-full flex flex-col space-y-3">
                                <div className="flex justify-between items-start">
                                    <div className="space-y-1">
                                        <h3 className="font-semibold text-lg text-white line-clamp-2">{event.title}</h3>
                                        <p className="text-sm text-secondary-400">{event.date} • {event.startTime}</p>
                                    </div>
                                    <span className={`badge ${getStatusBadgeClass(event.status || 'scheduled')}`}>
                                        {(event.status || 'scheduled').toUpperCase()}
                                    </span>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {event.games && event.games.map(g => (
                                        <span key={g} className="badge bg-secondary-900 text-secondary-300">
                                            {g}
                                        </span>
                                    ))}
                                </div>

                                <div className="mt-auto pt-2">
                                    {event.location && (
                                        <p className="text-sm text-secondary-400 line-clamp-1 flex items-center gap-1">
                                            📍 {event.location}
                                        </p>
                                    )}
                                    {event.startggUrl && (
                                        <p className="text-xs text-blue-400 mt-1 flex items-center gap-1">
                                            🔗 Linked to Start.gg
                                        </p>
                                    )}
                                </div>
                            </div>
                        </Link>
                    ))}
                    {events.length === 0 && (
                        <div className="col-span-full text-center py-12 card bg-secondary-800/50 border-dashed">
                            <p className="text-secondary-400">No events found matching your filter.</p>
                        </div>
                    )}
                </div>
            )}

            <StartggImportModal
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                onSuccess={() => {
                    // Snapshot will auto-update
                    setFilter('upcoming');
                }}
            />
        </div>
    );
}
